'use client';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import clsx from 'clsx';
import { Triangle } from 'lucide-react';
import { FC, ReactNode, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

export interface ColumnsProps {
  title: ReactNode;
  renderNode?: (row: any, index?: number) => ReactNode;
  key: string | number;
  styles?: string;
  sortKey?: string;
  isHidden?: boolean;
}

interface IShadcnUITableProps {
  rows: any[];
  columns: ColumnsProps[];
  noFoundMsg?: string;
  customRenderRow?: (row: any, index: number) => ReactNode;
  selectedRows?: number[];
  className?: string;
  onRowClick?: (row: any) => void;
  isLoading?: boolean;
  skeletonLoading?: boolean;
  skeletonRows?: number;
  onSortByApi?: (sortBy: string, isAscending: boolean) => void;
  getRowClassName?: (row: any, index: number) => string;
}

interface ISortIconProps {
  isAscending?: boolean;
  isDescending?: boolean;
}

const SortIcon = ({ isAscending, isDescending }: ISortIconProps) => {
  return (
    <div className='flex flex-col'>
      <Triangle
        size={8}
        fill={isAscending ? 'black' : '#ADB5BD'}
        color={isAscending ? 'black' : '#ADB5BD'}
      />
      <Triangle
        size={8}
        className='rotate-180'
        color={isDescending ? 'black' : '#ADB5BD'}
        fill={isDescending ? 'black' : '#ADB5BD'}
      />
    </div>
  );
};

const ShadcnUITable: FC<IShadcnUITableProps> = ({
  rows = [],
  columns = [],
  customRenderRow,
  selectedRows,
  className,
  onRowClick,
  isLoading = false,
  skeletonLoading = false,
  skeletonRows = 6,
  noFoundMsg = 'No data found',
  onSortByApi,
  getRowClassName,
}) => {
  const [sortedRows, setSortedRows] = useState(rows);
  const [sortBy, setSortBy] = useState('');
  const [isAscending, setIsAscending] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    setSortedRows(rows);
  }, [rows]);

  const handleSort = (sortKey: string) => {
    const newIsAscending = sortBy === sortKey ? !isAscending : true;

    setSortBy(sortKey);
    setIsAscending(newIsAscending);

    if (onSortByApi) {
      onSortByApi(sortKey, newIsAscending);
      return;
    }

    setSortedRows(sortArray(rows, { sortBy: sortKey, isAscending: newIsAscending }));
  };

  const sortArray = (array: any[], options?: { sortBy?: string; isAscending?: boolean }) => {
    const { sortBy = '', isAscending = true } = options || {};
    const sortedArray = array.sort((a, b) => {
      if (a[sortBy] < b[sortBy]) {
        return isAscending ? -1 : 1;
      }
      if (a[sortBy] > b[sortBy]) {
        return isAscending ? 1 : -1;
      } else {
        return 0;
      }
    });

    return sortedArray;
  };

  return (
    <Table className={className}>
      <TableHeader className='sticky top-0 bg-gray-50'>
        <TableRow className='bg-gray-50'>
          {columns
            .filter((col) => !col.isHidden)
            .map((column: ColumnsProps, index: number) => (
              <TableHead
                key={index}
                className={clsx('font-semibold text-gray-700 bg-gray-50', column?.styles || '')}
              >
                {column?.sortKey ? (
                  <Button
                    variant='inline'
                    className='flex items-center gap-2'
                    disabledpadding
                    type='button'
                    onClick={() => handleSort(column.sortKey as string)}
                  >
                    <span>{column.title}</span>
                    <SortIcon
                      isAscending={isAscending && sortBy === column.sortKey}
                      isDescending={isAscending === false && sortBy === column.sortKey}
                    />
                    {/* <Image src='/icons/sort-icon.svg' alt='' width={7} height={7} /> */}
                  </Button>
                ) : (
                  column.title
                )}
              </TableHead>
            ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRows.length === 0 && !isLoading && !skeletonLoading && (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              className='text-center text-sm text-gray-600'
              rowSpan={99999}
            >
              {noFoundMsg}
            </TableCell>
          </TableRow>
        )}
        {skeletonLoading &&
          Array.from({ length: skeletonRows }).map((_, index) => (
            <TableRow key={index}>
              {columns
                .filter((col) => !col.isHidden)
                .map((column: ColumnsProps, index: number) => (
                  <TableCell key={index}>
                    <Skeleton className='w-[90%] h-4' />
                  </TableCell>
                ))}
            </TableRow>
          ))}
        {!skeletonLoading &&
          sortedRows.map((row: any, rowIndex: number) =>
            customRenderRow ? (
              customRenderRow(row, rowIndex)
            ) : (
              <TableRow
                key={rowIndex}
                className={clsx('hover:bg-gray-50 transition-colors duration-150 group', {
                  'bg-primary-100': selectedRows?.includes(rowIndex),
                  'hover:cursor-pointer': !!onRowClick,
                }, getRowClassName?.(row, rowIndex))}
                onClick={() => onRowClick?.(row)}
              >
                {columns
                  .filter((col) => !col.isHidden)
                  .map((column: ColumnsProps, index: number) => (
                    <TableCell
                      key={index}
                      className={clsx('font-medium text-gray-600', column.styles || '')}
                    >
                      {isLoading ? (
                        <Skeleton className='w-[90%] h-4' />
                      ) : column?.renderNode ? (
                        column.renderNode(row, rowIndex)
                      ) : (
                        row[column.key]
                      )}
                    </TableCell>
                  ))}
              </TableRow>
            ),
          )}
      </TableBody>
    </Table>
  );
};

export default ShadcnUITable;
